// Otto's conversational state, computed in the browser with no network delay
// (design 7.2, 4.8 "visible reaction about 0.27 s"). Pure and clock-injected so
// every rule is unit tested.
//
//   listening  the owner's RAW mic level is above the gate threshold
//   thinking   250 ms of owner quiet on the raw level (not the gate, whose
//              600 ms hangover would delay it) and no Otto audio yet
//   speaking   Otto's audio is playing (the player worklet says so)
//   handoff    the moment Otto's turn ends: upper-lid raise and head lift
//   idle       none of the above
//
// Both the 3D Otto (avatarMotion.ts) and the 2D card read the snapshot.

export type OttoState = "idle" | "listening" | "thinking" | "speaking" | "handoff";

export const OTTO_STATE_TIMING = Object.freeze({
  /** Owner quiet this long (raw level) turns listening into thinking (design 7.2). */
  thinkingAfterQuietMs: 250,
  /** The owner must have spoken at least this long for a pause to mean "my turn is over". */
  minOwnerSpeechMs: 200,
  /** A thinking look that gets no reply fades back to idle after this long. */
  thinkingMaxMs: 12_000,
  /** How long the handoff look holds after Otto's last sound. */
  handoffMs: 1_400,
  /** Short pauses inside an answer: a nod at about 40 percent of them (design 7.2). */
  nodPauseMinMs: 150,
  nodPauseMaxMs: 600,
});

export interface OttoStateSnapshot {
  state: OttoState;
  /** When the current state began (ms, the caller's clock). */
  since: number;
  /** The owner's latest raw level as a 0..1 meter value. */
  ownerLevel: number;
  /** Increments each time the owner resumes after a 150 to 600 ms pause (a nod cue). */
  shortPauseCount: number;
  /** Increments on each owner turn that ends in thinking; lets the avatar time a slow nod. */
  thinkCount: number;
}

export interface OttoStateOptions {
  /** Raw RMS the owner's voice must reach; the gate threshold from MicSettings. */
  voiceThreshold: number;
}

export function createOttoStateMachine(options: OttoStateOptions, start = 0) {
  const timing = OTTO_STATE_TIMING;
  const snapshot: OttoStateSnapshot = { state: "idle", since: start, ownerLevel: 0, shortPauseCount: 0, thinkCount: 0 };
  let ottoPlaying = false;
  let muted = false;
  let voiceStartedAt: number | null = null;
  let lastVoiceAt = Number.NEGATIVE_INFINITY;
  let ownerSpokeMs = 0;
  let handoffUntil = Number.NEGATIVE_INFINITY;

  const set = (state: OttoState, now: number) => {
    if (snapshot.state === state) return;
    snapshot.state = state;
    snapshot.since = now;
    if (state === "thinking") snapshot.thinkCount += 1;
  };

  const settle = (now: number) => {
    if (ottoPlaying) return set("speaking", now);
    if (!muted && voiceStartedAt !== null && now - lastVoiceAt < timing.thinkingAfterQuietMs)
      return set("listening", now);
    if (!muted && ownerSpokeMs >= timing.minOwnerSpeechMs && now - lastVoiceAt >= timing.thinkingAfterQuietMs) {
      if (snapshot.state === "thinking" && now - snapshot.since >= timing.thinkingMaxMs) {
        ownerSpokeMs = 0;
        return set("idle", now);
      }
      return set("thinking", now);
    }
    if (now < handoffUntil) return set("handoff", now);
    return set("idle", now);
  };

  return {
    snapshot,
    /** One mic frame's raw RMS. Call it for every frame, even quiet ones. */
    micFrame(frameRms: number, now: number) {
      snapshot.ownerLevel = muted ? 0 : Math.min(1, frameRms * 4);
      const voice = !muted && frameRms >= options.voiceThreshold;
      if (voice && !ottoPlaying) {
        if (voiceStartedAt === null) {
          const pause = now - lastVoiceAt;
          if (ownerSpokeMs > 0 && pause >= timing.nodPauseMinMs && pause <= timing.nodPauseMaxMs)
            snapshot.shortPauseCount += 1;
          voiceStartedAt = now;
        }
        ownerSpokeMs += Number.isFinite(lastVoiceAt) && now - lastVoiceAt < 100 ? now - lastVoiceAt : 20;
        lastVoiceAt = now;
        handoffUntil = Number.NEGATIVE_INFINITY;
      } else if (voiceStartedAt !== null && now - lastVoiceAt >= timing.thinkingAfterQuietMs) {
        voiceStartedAt = null;
      }
      settle(now);
    },
    /** From the player worklet's "playing" and "idle" messages. */
    setOttoPlaying(playing: boolean, now: number) {
      if (playing === ottoPlaying) return;
      ottoPlaying = playing;
      if (playing) {
        // Otto answered: the owner's turn is over.
        ownerSpokeMs = 0;
        voiceStartedAt = null;
      } else {
        handoffUntil = now + timing.handoffMs;
      }
      settle(now);
    },
    setMuted(value: boolean, now: number) {
      muted = value;
      if (muted) {
        voiceStartedAt = null;
        ownerSpokeMs = 0;
        snapshot.ownerLevel = 0;
      }
      settle(now);
    },
    /** Re-evaluate on a timer (the handoff and thinking time-outs need no frame to expire). */
    tick(now: number) {
      settle(now);
    },
  };
}

export type OttoStateMachine = ReturnType<typeof createOttoStateMachine>;
